import { NEW_BASE_URL } from "../config";
import { createBackendRequest } from "../mappers/backendMappers";
import { parseApiProduct, parseApiProducts } from "../constants/parse/product";
import { generalHeader } from "../cycles/headers";
import { decodeBase64Utf8 } from "../utils/swaggerUtils";

// Hardcoded list of questionnaire category names
const QUESTIONNAIRE_CATEGORIES = [
  "Open Banking",
  "Payments",
  "Account Information",
  "Cards",
  "Loans",
  "Deposits",
  "Transfers",
  "Digital Banking",
];

class APIProductService {
  constructor() {
    this.productList = null;
    this.categoryList = null;
    this.featuredProductList = null;
    this.productDocument = null;
    this.apiDocument = null;
  }

  /**
   * Creates a request for the asset management API
   * @private
   */
  createAssetManagementRequest(payload, userId) {
    return {
      header: {
        id: this.generateGuid(),
        application: "03FC0B90-DFAD-11EE-8D86-0800200C9A66",
        userId: userId || "00000000-0000-0000-0000-000000000000",
      },
      payload: this.convertPayloadToCamelCase(payload),
    };
  }

  /**
   * Generates a GUID
   * @private
   */
  generateGuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  /**
   * Converts PascalCase payload to camelCase for asset management API
   * @private
   */
  convertPayloadToCamelCase(payload) {
    const converted = {};
    for (const key in payload) {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      converted[camelKey] = payload[key];
    }
    return converted;
  }

  /**
   * Converts asset management API response to match expected format
   * @private
   */
  convertAssetResponse(response, dataKey) {
    if (response.success === false) {
      throw new Error(response.error || "API request failed");
    }
    const data = dataKey ? response[dataKey] : response.data;
    return {
      Payload: data,
    };
  }

  /**
   * Fetches the list of all API Products
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @returns {Promise<Object>} The list of products
   */
  async getProductList(userId, csrfToken) {
    if (this.productList) {
      return this.productList;
    }

    try {
      // Create backend request with PascalCase payload
      const requestBody = createBackendRequest(
        {
          PageLimit: 1000,
          PageOffset: 0,
          SortField: "productName",
          SortOrder: "asc",
        },
        userId
      );

      const response = await fetch(`${NEW_BASE_URL}/Product/list`, {
        method: "POST",
        headers: {
          ...generalHeader(csrfToken, true),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle backend response format (Response<ReturnListProductDto>)
      let products = [];
      if (data.Payload && data.Payload.Data) {
        products = data.Payload.Data;
      } else if (data.payload && data.payload.data) {
        products = data.payload.data;
      } else {
        throw new Error("No product list data found in response");
      }

      // Store the raw product list and return it
      this.productList = products;
      return this.productList;
    } catch (error) {
      console.error("Error fetching product list:", error);
      throw error;
    }
  }

  refreshProductList(userId, csrfToken) {
    this.productList = null;
    return this.getProductList(userId, csrfToken);
  }

  /**
   * Gets the stored product list
   * @returns {Object|null} The stored product list
   */
  getStoredProductList() {
    return this.productList;
  }

  /**
   * Gets all products from the cached list
   * @returns {Array} All products or empty array
   */
  getAllProducts() {
    if (!this.productList) {
      console.warn("No products loaded. Call getProductList first.");
      return [];
    }

    // Return the products array
    if (Array.isArray(this.productList)) {
      return this.productList;
    }

    // If it's still wrapped in an object, extract the array
    if (this.productList?.data) {
      return this.productList.data;
    }

    if (this.productList?.Products) {
      return this.productList.Products;
    }

    return [];
  }

  /**
   * Gets the list of all Product Categories by extracting them from products
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @returns {Promise<Array>} The list of categories
   */
  async getCategoryList(userId, csrfToken) {
    if (this.categoryList) {
      return this.categoryList;
    }

    try {
      // Ensure we have products loaded first
      await this.getProductList(userId, csrfToken);

      // Extract unique categories from all products
      const categoryMap = new Map();
      const products = this.getAllProducts();

      products.forEach((product) => {
        const categories =
          product.productCategories || product.ProductCategories || [];
        categories.forEach((cat) => {
          const name =
            typeof cat === "string"
              ? cat
              : cat.name || cat.Name || cat.CategoryName;
          if (name && !categoryMap.has(name)) {
            categoryMap.set(name, {
              id: name, // Use name as ID
              Id: name, // Include both cases for compatibility
              name: name,
              Name: name, // Include both cases for compatibility
            });
          }
        });
      });

      // Convert map to array and sort alphabetically
      this.categoryList = Array.from(categoryMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      return this.categoryList;
    } catch (error) {
      console.error("Error getting category list from products:", error);
      throw error;
    }
  }

  /**
   * Gets the stored category list
   * @returns {Array|null} The stored category list
   */
  getStoredCategoryList() {
    return this.categoryList;
  }

  /**
   * Refreshes the category list
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @returns {Promise<Array>} The fresh list of categories
   */
  refreshCategoryList(userId, csrfToken) {
    this.categoryList = null;
    return this.getCategoryList(userId, csrfToken);
  }

  /**
   * Gets categories specifically for the questionnaire using hardcoded list
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @returns {Promise<Array>} The list of questionnaire categories
   */
  async getQuestionnaireCategories(userId, csrfToken) {
    try {
      // First ensure we have the full category list cached
      await this.getCategoryList(userId, csrfToken);

      // Get all categories from the cached list
      const allCategories = this.getStoredCategoryList() || [];

      // Filter categories to only include those in the hardcoded questionnaire list
      const questionnaireCategories = allCategories.filter((cat) => {
        return QUESTIONNAIRE_CATEGORIES.includes(cat.name);
      });

      return questionnaireCategories;
    } catch (error) {
      console.error("Error fetching questionnaire categories:", error);
      throw error;
    }
  }

  /**
   * Fetches featured API Products by filtering the cached product list
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @returns {Promise<Array>} The list of featured products (raw data)
   */
  async getFeaturedProducts(userId, csrfToken) {
    if (this.featuredProductList) {
      return this.featuredProductList;
    }

    try {
      // First ensure we have the full product list cached
      await this.getProductList(userId, csrfToken);

      // Get all products from the cached list
      const allProducts = this.getAllProducts();

      // Filter products where promote field is true
      const featuredProducts = allProducts.filter((product) => {
        // Check both uppercase and lowercase versions of the promote field
        return (
          product.promote === true ||
          product.Promote === true ||
          product.promoted === true ||
          product.Promoted === true
        );
      });

      // Cache the filtered featured products
      this.featuredProductList = featuredProducts;

      return this.featuredProductList;
    } catch (error) {
      console.error("Error fetching featured products:", error);
      throw error;
    }
  }

  /**
   * Gets the stored featured product list
   * @returns {Array|null} The stored featured product list
   */
  getStoredFeaturedProductList() {
    return this.featuredProductList;
  }

  /**
   * Refreshes the featured product list
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @returns {Promise<Array>} The fresh list of featured products
   */
  refreshFeaturedProductList(userId, csrfToken) {
    this.featuredProductList = null;
    // Also refresh the main product list to ensure we have the latest data
    this.productList = null;
    return this.getFeaturedProducts(userId, csrfToken);
  }

  /**
   * Gets products filtered by category from the cached product list
   * @param {string|Object} category - The category name or category object with value property
   * @returns {Array} The filtered and parsed products
   */
  getProductsByCategory(category) {
    if (!this.productList) {
      console.warn("No products loaded. Call getProductList first.");
      return [];
    }

    // Extract category identifier from category object or use directly if string
    // Since we now use name as ID, value will be the name
    const categoryId =
      typeof category === "object"
        ? category?.value || category?.name
        : category;

    // Get the products array from various possible response structures
    let products = [];
    if (Array.isArray(this.productList)) {
      products = this.productList;
    } else if (this.productList?.data) {
      products = this.productList.data;
    } else if (this.productList?.Products) {
      products = this.productList.Products;
    } else if (this.productList?.products) {
      products = this.productList.products;
    }

    // If no category specified, return all products parsed
    if (!categoryId) {
      return parseApiProducts(products);
    }

    // Filter products by category
    const filtered = products.filter((product) => {
      // Get product categories (handle both uppercase and lowercase)
      const categories =
        product.productCategories ||
        product.ProductCategories ||
        product.categories ||
        product.Categories ||
        [];

      // Check if product belongs to the selected category
      return categories.some((cat) => {
        if (!cat) return false;

        // Handle both string categories and object categories
        const catName =
          typeof cat === "string"
            ? cat
            : cat.name || cat.Name || cat.CategoryName;

        // Match by name (which is now our primary identifier), ignoring spaces and case:
        // filter links used to drop the spaces ("?filterName=CustomerData")
        const sameName = (a, b) =>
          typeof a === "string" &&
          typeof b === "string" &&
          a.replace(/\s+/g, "").toLowerCase() ===
            b.replace(/\s+/g, "").toLowerCase();

        if (sameName(catName, categoryId)) {
          return true;
        }

        // Also check if the category object has a matching name
        if (
          typeof category === "object" &&
          category.name &&
          sameName(catName, category.name)
        ) {
          return true;
        }

        // Legacy support: still check UUID/ID if they exist and match
        if (cat.uuid && cat.uuid === categoryId) {
          return true;
        }
        if (
          (cat.id && cat.id === categoryId) ||
          (cat.Id && cat.Id === categoryId)
        ) {
          return true;
        }

        return false;
      });
    });

    return parseApiProducts(filtered);
  }

  /**
   * Fetches API Product information by UUID
   * @param {string} uuid - The UUID of the API Product
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @returns {Promise<Object>} The product document
   */
  async getProductDocument(uuid, userId, csrfToken) {
    try {
      // Create backend request with PascalCase payload
      const requestBody = createBackendRequest(
        {
          Id: uuid,
        },
        userId
      );

      const response = await fetch(`${NEW_BASE_URL}/Product/get`, {
        method: "POST",
        headers: {
          ...generalHeader(csrfToken, true),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      let productData = null;

      // Handle backend response format (Response<ProductDto>)
      if (data.Payload) {
        productData = data.Payload;
      } else if (data.payload) {
        productData = data.payload;
      }

      if (!productData) {
        throw new Error("No product data found in response");
      }

      // Ensure the product has the expected ID field
      if (!productData.id && !productData.Id) {
        productData.id = uuid;
        productData.Id = uuid;
      }

      // Store backend data and return with mapping
      this.productDocument = productData;
      this.apiDocument = null;
      return {
        original: productData,
        mapped: parseApiProduct(productData),
      };
    } catch (error) {
      console.error("Error fetching API Product:", error);
      throw error;
    }
  }

  /**
   * Gets the currently loaded product document
   * @returns {Object|null} The product document
   */
  getStoredProductDocument() {
    return this.productDocument;
  }

  normalizeAlias(value) {
    if (!value) return null;

    return String(value)
      .replace(/^\/apiProduct\//, "")
      .replace(/^\/+|\/+$/g, "")
      .toLowerCase();
  }

  /**
   * Finds an API endpoint by name within the loaded product document
   * @param {string} apiName - The name of the API endpoint to find (can be URL encoded)
   * @returns {Object|null} The found API endpoint or null if not found
   */

  findAPI(apiIdentifier) {
    if (!this.productDocument) {
      console.warn(
        "No product document loaded. Call getProductDocument first."
      );
      this.apiDocument = null;
      return null;
    }

    let decodedIdentifier;
    try {
      decodedIdentifier = decodeURIComponent(apiIdentifier);
    } catch (e) {
      decodedIdentifier = apiIdentifier;
    }

    const normalizedIdentifier = this.normalizeAlias(decodedIdentifier);

    const apiEndpoints =
      this.productDocument?.apiEndpoints ||
      this.productDocument?.ApiEndpoints ||
      [];

    const foundApi = apiEndpoints.find((endpoint) => {
      const endpointName = endpoint.apiName || endpoint.ApiName;
      const endpointAlias =
        endpoint.urlAlias || endpoint.UrlAlias || endpoint.alias;

      return (
        endpointName === decodedIdentifier ||
        this.normalizeAlias(endpointAlias) === normalizedIdentifier
      );
    });

    if (foundApi) {
      this.apiDocument = foundApi;
      return this.apiDocument;
    }

    this.apiDocument = null;
    return null;
  }

  /**
   * Gets the currently loaded API document
   * @returns {Object|null} The API document
   */
  getApiDocument() {
    return this.apiDocument;
  }

  /**
   * Gets and decodes the Swagger specification from the current API document
   * @returns {Object|null} The decoded Swagger specification or null if not available
   */
  getSwagger() {
    if (!this.apiDocument) {
      console.warn("No API document loaded. Call findAPI first.");
      return null;
    }

    // Check for swagger property (handle both uppercase and lowercase)
    const swagger = this.apiDocument.swagger || this.apiDocument.Swagger;

    if (!swagger) {
      console.warn("No swagger property found in API document.");
      return null;
    }

    // Get fileData property (handle both uppercase and lowercase)
    let fileData = swagger.fileData || swagger.FileData;

    if (!fileData) {
      console.warn("No fileData property found in swagger object.");
      return null;
    }

    try {
      // Check if fileData is a data URL (e.g., "data:application/json;base64,...")
      if (typeof fileData === "string" && fileData.startsWith("data:")) {
        // Extract the base64 part from the data URL
        const base64Index = fileData.indexOf("base64,");
        if (base64Index !== -1) {
          fileData = fileData.substring(base64Index + 7); // Skip "base64,"
        } else {
          // If it's a data URL but not base64, try to extract the data part
          const commaIndex = fileData.indexOf(",");
          if (commaIndex !== -1) {
            fileData = fileData.substring(commaIndex + 1);
          }
        }
      }

      // Decode base64 fileData to get the Swagger JSON
      const decodedString = decodeBase64Utf8(fileData);
      const swaggerSpec = JSON.parse(decodedString);

      return swaggerSpec;
    } catch (error) {
      console.error("Error decoding swagger fileData:", error);

      // Try to handle if it's not base64 encoded
      try {
        // If it's already a JSON string
        if (typeof fileData === "string") {
          return JSON.parse(fileData);
        }
        // If it's already an object
        return fileData;
      } catch (fallbackError) {
        console.error(
          "Failed to parse swagger data in any format:",
          fallbackError
        );
        return null;
      }
    }
  }

  /**
   * Gets the Swagger description with the "Entities" section filtered out
   * @returns {string|null} The filtered description or null if not available
   */
  getSwaggerDescription() {
    // First get the swagger specification
    const swaggerSpec = this.getSwagger();

    if (!swaggerSpec) {
      console.warn("No Swagger specification available.");
      return null;
    }

    // Extract the description from info section
    const description = swaggerSpec?.info?.description;

    if (!description) {
      console.warn("No description found in Swagger info section.");
      return null;
    }

    // Filter out the "Entities" section
    // Look for "# Entities" (Level 1 markdown header) with optional leading spaces
    const entitiesPattern = /^\s*#\s+Entities\s*$/m;
    const entitiesMatch = description.match(entitiesPattern);

    if (!entitiesMatch) {
      // No Entities section found, return original description
      return description;
    }

    // Find the start position of the Entities header
    const entitiesStartIndex = description.indexOf(entitiesMatch[0]);

    // Look for the next Level 1 header after Entities (with optional leading spaces)
    const afterEntities = description.substring(
      entitiesStartIndex + entitiesMatch[0].length
    );
    const nextHeaderPattern = /^\s*#\s+[^#]/m;
    const nextHeaderMatch = afterEntities.match(nextHeaderPattern);

    let swaggerDescription;

    if (nextHeaderMatch) {
      // Found another Level 1 header after Entities
      const nextHeaderIndex = afterEntities.indexOf(nextHeaderMatch[0]);
      const absoluteNextHeaderIndex =
        entitiesStartIndex + entitiesMatch[0].length + nextHeaderIndex;

      // Remove the Entities section (from start of Entities to start of next header)
      swaggerDescription =
        description.substring(0, entitiesStartIndex) +
        description.substring(absoluteNextHeaderIndex);
    } else {
      // Entities is the last section, remove everything from Entities to the end
      swaggerDescription = description.substring(0, entitiesStartIndex);
    }

    // Trim any trailing whitespace
    swaggerDescription = swaggerDescription.trimEnd();

    return swaggerDescription;
  }

  /**
   * Downloads the Swagger specification as a JSON file
   * @returns {boolean} True if download was successful, false otherwise
   */
  downloadSwagger() {
    // Check if we have both swagger data and API document
    const swaggerSpec = this.getSwagger();
    if (!swaggerSpec || !this.apiDocument) {
      console.warn("No Swagger data or API document available to download");
      return false;
    }

    try {
      // Get the filename from the API document
      const swaggerData = this.apiDocument.swagger || this.apiDocument.Swagger;
      const fileName =
        swaggerData?.fileName || swaggerData?.FileName || "swagger.json";

      // Convert the swagger specification to formatted JSON string
      const jsonString = JSON.stringify(swaggerSpec, null, 2);

      // Create a blob from the JSON string
      const blob = new Blob([jsonString], { type: "application/json" });

      // Create a temporary URL for the blob
      const url = URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error("Error downloading Swagger file:", error);
      return false;
    }
  }

  /**
   * Downloads a single product document as JSON
   * @param {Object} productDocument - The product document to download (with original and mapped properties)
   * @returns {boolean} True if download was successful, false otherwise
   */
  downloadProduct(productDocument) {
    try {
      if (!productDocument) {
        console.warn("No product document provided to download");
        return false;
      }

      // Get the document to download (prefer original over mapped)
      const docToDownload =
        productDocument.original || productDocument.mapped || productDocument;

      // Get the productName from the document for filename
      const productName =
        docToDownload.productName ||
        docToDownload.ProductName ||
        docToDownload.Title ||
        docToDownload.title ||
        docToDownload.Name ||
        docToDownload.name ||
        "product_document";

      // Sanitize the productName for use as filename
      const sanitizedTitle = productName
        .replace(/[^a-z0-9_-]/gi, "_")
        .replace(/_+/g, "_")
        .trim();

      // Create JSON content
      const jsonContent = JSON.stringify(docToDownload, null, 2);
      const blob = new Blob([jsonContent], { type: "application/json" });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${sanitizedTitle}.json`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up URL
      window.URL.revokeObjectURL(url);

      console.log(`Downloaded product document: ${link.download}`);
      return true;
    } catch (error) {
      console.error("Error downloading product document:", error);
      return false;
    }
  }

  /**
   * Downloads all API Products as JSON documents
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @param {Function} onProgress - Optional callback for progress updates
   * @returns {Promise<Object>} Summary of downloaded products
   */
  async downloadAllAPIProducts(userId, csrfToken, onProgress) {
    try {
      // First, get the list of all products
      const productListResponse = await this.getProductList(userId, csrfToken);

      // Extract the actual array from the response (handle different response structures)
      const productList = Array.isArray(productListResponse)
        ? productListResponse
        : Array.isArray(productListResponse?.data)
        ? productListResponse.data
        : Array.isArray(productListResponse?.Data)
        ? productListResponse.Data
        : null;

      if (!productList) {
        throw new Error("Failed to fetch product list or invalid format");
      }

      const totalProducts = productList.length;
      const downloadedProducts = [];
      const failedProducts = [];

      console.log(`Starting download of ${totalProducts} API products...`);

      // Iterate through each product
      for (let i = 0; i < productList.length; i++) {
        const product = productList[i];
        const productId = product.Id || product.id;
        const productName = product.Name || product.name || `Product_${i}`;

        try {
          // Update progress if callback provided
          if (onProgress) {
            onProgress({
              current: i + 1,
              total: totalProducts,
              productName,
              status: "downloading",
            });
          }

          // Fetch the full product document
          const productDoc = await this.getProductDocument(
            productId,
            userId,
            csrfToken
          );

          if (productDoc) {
            // Get the document data (prefer original)
            const docData = productDoc.original || productDoc;

            // Get productName from the document itself, not from the list
            const docProductName =
              docData.productName ||
              docData.ProductName ||
              docData.Title ||
              docData.title ||
              docData.Name ||
              docData.name ||
              productName ||
              `Product_${i}`;

            // Create a blob from the JSON data
            const jsonContent = JSON.stringify(docData, null, 2);
            const blob = new Blob([jsonContent], { type: "application/json" });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Use productName from document for filename, sanitizing it for filesystem
            const sanitizedName = docProductName
              .replace(/[^a-z0-9_-]/gi, "_")
              .replace(/_+/g, "_")
              .trim();
            link.download = `${sanitizedName}.json`;

            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up URL
            window.URL.revokeObjectURL(url);

            downloadedProducts.push({
              id: productId,
              name: docProductName,
              fileName: link.download,
            });

            // Add a small delay between downloads to avoid overwhelming the browser
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`Failed to download product ${productName}:`, error);
          failedProducts.push({
            id: productId,
            name: productName,
            error: error.message,
          });
        }
      }

      // Final progress update
      if (onProgress) {
        onProgress({
          current: totalProducts,
          total: totalProducts,
          status: "completed",
          downloaded: downloadedProducts.length,
          failed: failedProducts.length,
        });
      }

      const summary = {
        total: totalProducts,
        downloaded: downloadedProducts,
        failed: failedProducts,
        success: failedProducts.length === 0,
      };

      console.log("Download summary:", summary);
      return summary;
    } catch (error) {
      console.error("Error downloading API products:", error);
      throw error;
    }
  }

  /**
   * Fetches API Product by API endpoint alias
   * @param {string} apiEndpointAlias - The API endpoint alias (e.g., "/documentation/Account-Information-Berlin-Group-v1")
   * @param {string} userId - The user ID for the request
   * @param {string} csrfToken - The CSRF token for authentication
   * @returns {Promise<Object>} The product document with { original, mapped }
   */
  async getProductByApiAlias(apiEndpointAlias, userId, csrfToken) {
    try {
      // Create backend request with PascalCase payload
      const requestBody = createBackendRequest(
        {
          ApiEndpointAlias: apiEndpointAlias,
        },
        userId
      );

      const response = await fetch(`${NEW_BASE_URL}/Product/getByApiAlias`, {
        method: "POST",
        headers: {
          ...generalHeader(csrfToken, true),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      let productData = null;

      // Handle backend response format (Response<ProductDto>)
      if (data.Payload) {
        productData = data.Payload;
      } else if (data.payload) {
        productData = data.payload;
      }

      if (!productData) {
        throw new Error("No product data found in response");
      }

      // Ensure the product has the expected ID field
      if (!productData.id && !productData.Id) {
        const uuid = productData.Id || productData.id;
        if (uuid) {
          productData.id = uuid;
          productData.Id = uuid;
        }
      }

      // Store backend data and return with mapping
      this.productDocument = productData;
      this.apiDocument = null;
      return {
        original: productData,
        mapped: parseApiProduct(productData),
      };
    } catch (error) {
      console.error(
        "Error fetching API Product by alias:",
        apiEndpointAlias,
        error
      );
      throw error;
    }
  }

  // Public, deprecated-inclusive: resolves a urlAlias to a product regardless of deprecated
  // status. getProductList() above deliberately excludes deprecated products (by design,
  // unchanged) - this is the fallback for a direct link to one, so its own page still loads
  // instead of 404ing outright. See devportal-manager-cms-feature memory. Throws on a miss,
  // same as getProductByApiAlias above - callers should wrap this in their own try/catch and
  // treat a throw as "no match", not a fatal error.
  async getProductByUrlAlias(urlAlias, userId, csrfToken) {
    const requestBody = createBackendRequest({ UrlAlias: urlAlias }, userId);

    const response = await fetch(`${NEW_BASE_URL}/Product/getByUrlAlias`, {
      method: "POST",
      headers: {
        ...generalHeader(csrfToken, true),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const productData = data.Payload || data.payload;

    if (!productData) {
      throw new Error("No product data found in response");
    }

    this.productDocument = productData;
    this.apiDocument = null;
    return {
      original: productData,
      mapped: parseApiProduct(productData),
    };
  }

  /**
   * Clears the service state
   */
  clear() {
    this.productDocument = null;
    this.apiDocument = null;
    this.productList = null;
    this.categoryList = null;
    this.featuredProductList = null;
  }
}

export default new APIProductService();
